import React, { Suspense, createContext, useContext, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Link, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import Layout from './shared/components/Layout'
import ErrorBoundary from './shared/components/ErrorBoundary'
import CommandPalette from './shared/components/CommandPalette'
import FeedbackModal from './shared/components/FeedbackModal'
import { AuthProvider, useAuth } from './context/AuthContext'
import ProtectedRoute from './modules/auth/ProtectedRoute'
import ProRoute from './modules/auth/ProRoute'

// ── Palette context — lets sidebar/home page open the palette with an optional query
export const PaletteContext = createContext<{ openPalette: (query?: string) => void }>({ openPalette: () => {} })
export const usePalette = () => useContext(PaletteContext)

// ── Feedback context — lets any component open the feedback modal
export const FeedbackContext = createContext<{ openFeedback: () => void }>({ openFeedback: () => {} })
export const useFeedback = () => useContext(FeedbackContext)

// ── Existing tools ─────────────────────────────────────────────────────────────

const JsonFormatterPage = React.lazy(
  () => import('./modules/tools-playground/pages/JsonFormatterPage'),
)
const JwtDecoderPage = React.lazy(
  () => import('./modules/tools-playground/pages/JwtDecoderPage'),
)
const UuidGeneratorPage = React.lazy(
  () => import('./modules/tools-playground/pages/UuidGeneratorPage'),
)
const RegexTesterPage = React.lazy(
  () => import('./modules/tools-playground/pages/RegexTesterPage'),
)
const Base64CodecPage = React.lazy(
  () => import('./modules/tools-playground/pages/Base64CodecPage'),
)
const ApiLabPage = React.lazy(
  () => import('./modules/api-lab/ApiLabPage'),
)
const LoginPage = React.lazy(
  () => import('./modules/auth/LoginPage'),
)
const RegisterPage = React.lazy(
  () => import('./modules/auth/RegisterPage'),
)
const BillingPage = React.lazy(
  () => import('./modules/billing/BillingPage'),
)

// ── Home / Dashboard ──────────────────────────────────────────────────────────

const HomePage = React.lazy(() => import('./pages/HomePage'))

// ── Track A — Pure-frontend tools ────────────────────────────────────────────

// Formatters
const HtmlFormatterPage   = React.lazy(() => import('./modules/tools/html-formatter/HtmlFormatterPage'))
const SqlFormatterPage    = React.lazy(() => import('./modules/tools/sql-formatter/SqlFormatterPage'))
const MarkdownPreviewPage = React.lazy(() => import('./modules/tools/markdown-preview/MarkdownPreviewPage'))
const YamlJsonPage        = React.lazy(() => import('./modules/tools/yaml-json/YamlJsonPage'))

// Encoders/Decoders
const UrlEncoderPage    = React.lazy(() => import('./modules/tools/url-encoder/UrlEncoderPage'))
const HtmlEntitiesPage  = React.lazy(() => import('./modules/tools/html-entities/HtmlEntitiesPage'))

// Generators
const HashGeneratorPage     = React.lazy(() => import('./modules/tools/hash-generator/HashGeneratorPage'))
const PasswordGeneratorPage = React.lazy(() => import('./modules/tools/password-generator/PasswordGeneratorPage'))
const LoremIpsumPage        = React.lazy(() => import('./modules/tools/lorem-ipsum/LoremIpsumPage'))

// Converters
const TimestampPage     = React.lazy(() => import('./modules/tools/timestamp/TimestampPage'))
const ColorConverterPage= React.lazy(() => import('./modules/tools/color-converter/ColorConverterPage'))
const NumberBasePage    = React.lazy(() => import('./modules/tools/number-base/NumberBasePage'))
const StringCasePage    = React.lazy(() => import('./modules/tools/string-case/StringCasePage'))
const CsvJsonPage       = React.lazy(() => import('./modules/tools/csv-json/CsvJsonPage'))
const TextDiffPage      = React.lazy(() => import('./modules/tools/text-diff/TextDiffPage'))
const CurlConverterPage = React.lazy(() => import('./modules/tools/curl-converter/CurlConverterPage'))

// P7 — SQL Playground (dynamic sql.js import inside page component — PERF-P7-01)
const SqlPlaygroundPage = React.lazy(() => import('./modules/tools/sql-playground/SqlPlaygroundPage'))

// B-track — backend-calling tools
const HmacGeneratorPage = React.lazy(() => import('./modules/tools/hmac-generator/HmacGeneratorPage'))
const JwtGeneratorPage  = React.lazy(() => import('./modules/tools/jwt-generator/JwtGeneratorPage'))
const QrGeneratorPage   = React.lazy(() => import('./modules/tools/qr-generator/QrGeneratorPage'))
const TomlJsonPage      = React.lazy(() => import('./modules/tools/toml-json/TomlJsonPage'))

// Validators
const CronParserPage   = React.lazy(() => import('./modules/tools/cron-parser/CronParserPage'))
const HttpStatusPage   = React.lazy(() => import('./modules/tools/http-status/HttpStatusPage'))
const MimeLookupPage   = React.lazy(() => import('./modules/tools/mime-lookup/MimeLookupPage'))
const JsonToTsPage     = React.lazy(() => import('./modules/tools/json-to-ts/JsonToTsPage'))

// ── P8B — Client-side tools ───────────────────────────────────────────────────
// Python Runner: dynamic Pyodide import inside page component — PERF (same pattern as SQL Playground)
const PythonRunnerPage = React.lazy(() => import('./modules/tools/python-runner/PythonRunnerPage'))
const JsRunnerPage     = React.lazy(() => import('./modules/tools/js-runner/JsRunnerPage'))

// ── P9 — New tools (Phase 9) ──────────────────────────────────────────────────
// Screenshot route held — wire up after Backend confirms P9-01-04
const ScreenshotToolPage  = React.lazy(() => import('./modules/tools/screenshot/ScreenshotToolPage'))
const BatchProcessingPage = React.lazy(() => import('./modules/tools/batch/BatchProcessingPage'))

// ── P8A — New file/tool pages ─────────────────────────────────────────────────
const ImageCompressPage = React.lazy(() => import('./modules/tools/image-compress/ImageCompressPage'))
const PdfExtractorPage  = React.lazy(() => import('./modules/tools/pdf-extractor/PdfExtractorPage'))
const MyFilesPage       = React.lazy(() => import('./modules/files/MyFilesPage'))
const ApiKeysPage       = React.lazy(() => import('./modules/settings/ApiKeysPage'))

// ── P7 — File upload tools ────────────────────────────────────────────────────
const ImageBase64Page  = React.lazy(() => import('./modules/tools/image-base64/ImageBase64Page'))
const ImageExifPage    = React.lazy(() => import('./modules/tools/image-exif/ImageExifPage'))
const SvgOptimizerPage = React.lazy(() => import('./modules/tools/svg-optimizer/SvgOptimizerPage'))
const FileHashPage     = React.lazy(() => import('./modules/tools/file-hash/FileHashPage'))
const CsvLargePage     = React.lazy(() => import('./modules/tools/csv-large/CsvLargePage'))

// ── P7 — Network tools ────────────────────────────────────────────────────────
const DnsLookupPage     = React.lazy(() => import('./modules/tools/dns-lookup/DnsLookupPage'))
const SslInspectorPage  = React.lazy(() => import('./modules/tools/ssl-inspector/SslInspectorPage'))
const HttpHeadersPage   = React.lazy(() => import('./modules/tools/http-headers/HttpHeadersPage'))
const IpInfoPage        = React.lazy(() => import('./modules/tools/ip-info/IpInfoPage'))
const UrlMetadataPage   = React.lazy(() => import('./modules/tools/url-metadata/UrlMetadataPage'))

// ── P7 — Pro features ─────────────────────────────────────────────────────────
const SessionHistoryPage  = React.lazy(() => import('./modules/tools/session-history/SessionHistoryPage'))
const SharePage           = React.lazy(() => import('./modules/tools/share/SharePage'))
const WebhookTesterPage   = React.lazy(() => import('./modules/tools/webhook-tester/WebhookTesterPage'))

// ── Stripe redirect targets (AR-P5-04) ────────────────────────────────────────

// Poll /auth/me every 3s for up to 30s waiting for webhook to set plan = 'pro'.
function BillingSuccessPage() {
  const { user, refreshUser } = useAuth()
  const [upgraded, setUpgraded] = useState(user?.plan === 'pro')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const attemptsRef = useRef(0)

  useEffect(() => {
    if (upgraded) return
    intervalRef.current = setInterval(async () => {
      attemptsRef.current += 1
      try {
        await refreshUser()
      } catch { /* ignore */ }
      // refreshUser updates AuthContext; check via the user prop on next render
      if (attemptsRef.current >= 10) {
        // 10 × 3s = 30s maximum
        clearInterval(intervalRef.current!)
      }
    }, 3000)
    return () => clearInterval(intervalRef.current!)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Detect upgrade once user in context is refreshed
  useEffect(() => {
    if (user?.plan === 'pro') {
      setUpgraded(true)
      clearInterval(intervalRef.current!)
    }
  }, [user?.plan])

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      {upgraded ? (
        <>
          <p className="text-lg font-semibold text-success-300">You're now on Pro!</p>
          <Link to="/" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
            Back to DevHub
          </Link>
        </>
      ) : (
        <>
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-700 border-t-primary-500" />
          <p className="text-base font-semibold text-neutral-100">
            Payment received — your plan will update within a few minutes.
          </p>
          <p className="text-sm text-neutral-400">Checking for plan update…</p>
          <Link to="/" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
            Back to DevHub
          </Link>
        </>
      )}
    </div>
  )
}

function BillingCancelPage() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <p className="text-lg font-semibold text-neutral-100">
        Checkout cancelled — no charge was made.
      </p>
      <Link to="/" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
        Back to DevHub
      </Link>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function LayoutWrapper({ paletteOpen, setPaletteOpen, paletteQuery }: {
  paletteOpen: boolean
  setPaletteOpen: (v: boolean) => void
  paletteQuery: string
}) {
  return (
    <Layout>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} initialQuery={paletteQuery} />
      <Outlet />
    </Layout>
  )
}

function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>{children}</Suspense>
    </ErrorBoundary>
  )
}

export default function App() {
  const [paletteOpen,   setPaletteOpen]   = useState(false)
  const [paletteQuery,  setPaletteQuery]  = useState('')
  const [feedbackOpen,  setFeedbackOpen]  = useState(false)

  const openPalette = (query?: string) => {
    setPaletteQuery(query ?? '')
    setPaletteOpen(true)
  }

  const openFeedback = () => setFeedbackOpen(true)

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (paletteOpen) setPaletteOpen(false)
        else openPalette()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [paletteOpen])

  return (
    <AuthProvider>
      <FeedbackContext.Provider value={{ openFeedback }}>
      <PaletteContext.Provider value={{ openPalette }}>
      <BrowserRouter>
        <Routes>
          {/* ── Auth pages — no Layout sidebar ─────────────────────────────── */}
          <Route path="/login"    element={<LazyRoute><LoginPage /></LazyRoute>} />
          <Route path="/register" element={<LazyRoute><RegisterPage /></LazyRoute>} />

          {/* ── App pages — with Layout sidebar ────────────────────────────── */}
          <Route element={<LayoutWrapper paletteOpen={paletteOpen} setPaletteOpen={setPaletteOpen} paletteQuery={paletteQuery} />}>
            {/* Home */}
            <Route path="/" element={<LazyRoute><HomePage /></LazyRoute>} />

            {/* Existing tools */}
            <Route path="/tools/json-formatter" element={<LazyRoute><JsonFormatterPage /></LazyRoute>} />
            <Route path="/tools/jwt-decoder"    element={<LazyRoute><JwtDecoderPage /></LazyRoute>} />
            <Route path="/tools/uuid-generator" element={<LazyRoute><UuidGeneratorPage /></LazyRoute>} />
            <Route path="/tools/regex-tester"   element={<LazyRoute><RegexTesterPage /></LazyRoute>} />
            <Route path="/tools/base64"         element={<LazyRoute><Base64CodecPage /></LazyRoute>} />

            {/* API Lab */}
            <Route path="/lab" element={<LazyRoute><ApiLabPage /></LazyRoute>} />

            {/* Billing */}
            <Route path="/billing" element={<LazyRoute><ProtectedRoute><BillingPage /></ProtectedRoute></LazyRoute>} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancel"  element={<BillingCancelPage />} />

            {/* Formatters (A01–A04) */}
            <Route path="/tools/html-formatter"   element={<LazyRoute><HtmlFormatterPage /></LazyRoute>} />
            <Route path="/tools/sql-formatter"    element={<LazyRoute><SqlFormatterPage /></LazyRoute>} />
            <Route path="/tools/markdown-preview" element={<LazyRoute><MarkdownPreviewPage /></LazyRoute>} />
            <Route path="/tools/yaml-json"        element={<LazyRoute><YamlJsonPage /></LazyRoute>} />

            {/* Encoders/Decoders (A05–A06) */}
            <Route path="/tools/url-encoder"   element={<LazyRoute><UrlEncoderPage /></LazyRoute>} />
            <Route path="/tools/html-entities" element={<LazyRoute><HtmlEntitiesPage /></LazyRoute>} />

            {/* Generators (A07–A09) */}
            <Route path="/tools/hash-generator"     element={<LazyRoute><HashGeneratorPage /></LazyRoute>} />
            <Route path="/tools/password-generator" element={<LazyRoute><PasswordGeneratorPage /></LazyRoute>} />
            <Route path="/tools/lorem-ipsum"        element={<LazyRoute><LoremIpsumPage /></LazyRoute>} />

            {/* Converters (A10–A15) */}
            <Route path="/tools/timestamp"     element={<LazyRoute><TimestampPage /></LazyRoute>} />
            <Route path="/tools/color-converter"element={<LazyRoute><ColorConverterPage /></LazyRoute>} />
            <Route path="/tools/number-base"   element={<LazyRoute><NumberBasePage /></LazyRoute>} />
            <Route path="/tools/string-case"   element={<LazyRoute><StringCasePage /></LazyRoute>} />
            <Route path="/tools/csv-json"      element={<LazyRoute><CsvJsonPage /></LazyRoute>} />
            <Route path="/tools/text-diff"     element={<LazyRoute><TextDiffPage /></LazyRoute>} />
            <Route path="/tools/curl-converter"element={<LazyRoute><CurlConverterPage /></LazyRoute>} />

            {/* Validators (A16–A19) */}
            <Route path="/tools/cron-parser"  element={<LazyRoute><CronParserPage /></LazyRoute>} />
            <Route path="/tools/http-status"  element={<LazyRoute><HttpStatusPage /></LazyRoute>} />
            <Route path="/tools/mime-lookup"  element={<LazyRoute><MimeLookupPage /></LazyRoute>} />
            <Route path="/tools/json-to-ts"   element={<LazyRoute><JsonToTsPage /></LazyRoute>} />

            {/* P7 — SQL Playground */}
            <Route path="/tools/sql-playground" element={<LazyRoute><SqlPlaygroundPage /></LazyRoute>} />

            {/* B-track — backend-calling tools */}
            <Route path="/tools/hmac-generator"  element={<LazyRoute><HmacGeneratorPage /></LazyRoute>} />
            <Route path="/tools/jwt-generator"   element={<LazyRoute><JwtGeneratorPage /></LazyRoute>} />
            <Route path="/tools/qr-generator"    element={<LazyRoute><QrGeneratorPage /></LazyRoute>} />
            <Route path="/tools/toml-json"       element={<LazyRoute><TomlJsonPage /></LazyRoute>} />

            {/* P8B — Client-side tools */}
            <Route path="/tools/python-runner" element={<LazyRoute><PythonRunnerPage /></LazyRoute>} />
            <Route path="/tools/js-runner"     element={<LazyRoute><JsRunnerPage /></LazyRoute>} />

            {/* P7 — File upload tools */}
            <Route path="/tools/image-base64"  element={<LazyRoute><ImageBase64Page /></LazyRoute>} />
            <Route path="/tools/image-exif"    element={<LazyRoute><ImageExifPage /></LazyRoute>} />
            <Route path="/tools/svg-optimizer" element={<LazyRoute><SvgOptimizerPage /></LazyRoute>} />
            <Route path="/tools/file-hash"     element={<LazyRoute><FileHashPage /></LazyRoute>} />
            <Route path="/tools/csv-large"     element={<LazyRoute><CsvLargePage /></LazyRoute>} />

            {/* P7 — Network tools */}
            <Route path="/tools/dns-lookup"    element={<LazyRoute><DnsLookupPage /></LazyRoute>} />
            <Route path="/tools/ssl-inspector" element={<LazyRoute><SslInspectorPage /></LazyRoute>} />
            <Route path="/tools/http-headers"  element={<LazyRoute><HttpHeadersPage /></LazyRoute>} />
            <Route path="/tools/ip-info"       element={<LazyRoute><IpInfoPage /></LazyRoute>} />
            <Route path="/tools/url-metadata"  element={<LazyRoute><UrlMetadataPage /></LazyRoute>} />

            {/* P7 — Pro features */}
            <Route path="/history" element={<LazyRoute><ProRoute><SessionHistoryPage /></ProRoute></LazyRoute>} />
            <Route path="/share/:token" element={<LazyRoute><SharePage /></LazyRoute>} />
            <Route path="/tools/webhook-tester" element={<LazyRoute><ProRoute><WebhookTesterPage /></ProRoute></LazyRoute>} />

            {/* P9 — Batch Processing (no ProRoute) */}
            <Route path="/tools/batch" element={<LazyRoute><BatchProcessingPage /></LazyRoute>} />

            {/* P9 — Screenshot Tool (ProRoute — held until Backend confirms P9-01-04) */}
            <Route path="/tools/screenshot" element={<LazyRoute><ProRoute><ScreenshotToolPage /></ProRoute></LazyRoute>} />

            {/* P8A — File upload tools */}
            <Route path="/tools/image-compress" element={<LazyRoute><ImageCompressPage /></LazyRoute>} />
            <Route path="/tools/pdf-extractor"  element={<LazyRoute><PdfExtractorPage /></LazyRoute>} />

            {/* P8A — Pro features */}
            <Route path="/files"             element={<LazyRoute><ProRoute><MyFilesPage /></ProRoute></LazyRoute>} />
            <Route path="/settings/api-keys" element={<LazyRoute><ProRoute><ApiKeysPage /></ProRoute></LazyRoute>} />

            {/* Catch-all fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      </BrowserRouter>
      </PaletteContext.Provider>
      </FeedbackContext.Provider>
    </AuthProvider>
  )
}
